import React, { useState, useEffect } from 'react';
import { InvoiceItem, GelanggangDetail, UserProfile, PersatuanPaymentSettings } from '../../types';
import { useToast } from './Toast';
import { uploadYuranReceiptToAhliDriveJson } from '../../lib/gdrive';
import { saveInvoiceDB, saveTransactionDB, saveGelanggangDB, getPersatuanPaymentSettingsDB, DEFAULT_PERSATUAN_PAYMENT_SETTINGS } from '../../services/db';
import { sanitizeMemberId, parseReceiptTimestamp } from '../../utils/formatters';
import { processUploadedQrToSvg } from '../../utils/qrProcessor';
import { scanReceiptFile } from '../../utils/receiptScanner';
import {
  ArrowLeft,
  QrCode,
  Download,
  Copy,
  Check,
  Upload,
  FileText,
  AlertCircle,
  Loader2,
  Building2,
  Sparkles,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  Trash2,
  Info,
  ScanText,
  User,
  Calendar,
  Clock
} from 'lucide-react';

interface QrPaymentPageViewProps {
  invoice: InvoiceItem;
  gelanggang?: GelanggangDetail | null;
  onBack: () => void;
  onSuccess: (updatedInvoice: InvoiceItem) => void;
  currentUser?: UserProfile | null;
}

export const QrPaymentPageView: React.FC<QrPaymentPageViewProps> = ({
  invoice,
  gelanggang,
  onBack,
  onSuccess,
  currentUser
}) => {
  const { showToast } = useToast();
  const [uploadedReceiptFile, setUploadedReceiptFile] = useState<File | null>(null);
  const [receiptFilePreview, setReceiptFilePreview] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);

  // Auto-scan PDF states for Reference ID and Timestamp (initial fallback from invoice if available)
  const [detectedRefId, setDetectedRefId] = useState<string>(
    invoice.toyibBillCode && invoice.toyibBillCode !== 'ASMM-FPX-LOCAL'
      ? invoice.toyibBillCode
      : (invoice.transactionRef || '')
  );

  const initialParsed = parseReceiptTimestamp(invoice.paidAt || '');
  const [paidDate, setPaidDate] = useState<string>(initialParsed.date);
  const [paidTime, setPaidTime] = useState<string>(initialParsed.time);
  const [detectedTimestamp, setDetectedTimestamp] = useState<string>(initialParsed.full);

  const handleDateChange = (newDate: string) => {
    setPaidDate(newDate);
    const cleanTime = paidTime.length === 5 ? `${paidTime}:00` : (paidTime || '10:00:00');
    setDetectedTimestamp(`${newDate} ${cleanTime}`);
  };

  const handleTimeChange = (newTime: string) => {
    setPaidTime(newTime);
    const cleanTime = newTime.length === 5 ? `${newTime}:00` : (newTime || '10:00:00');
    setDetectedTimestamp(`${paidDate} ${cleanTime}`);
  };

  const [isScanningPdf, setIsScanningPdf] = useState<boolean>(false);
  const [scanProgress, setScanProgress] = useState<string>('');
  const [scanStatus, setScanStatus] = useState<'idle' | 'success' | 'empty' | 'error'>(
    invoice.toyibBillCode && invoice.toyibBillCode !== 'ASMM-FPX-LOCAL' ? 'success' : 'idle'
  );
  const [isAccountCopied, setIsAccountCopied] = useState<boolean>(false);
  const [isInvoiceCopied, setIsInvoiceCopied] = useState<boolean>(false);

  // Helper to extract month and year from invoice for standardized receipt naming
  const getInvoiceMonthYear = (inv?: InvoiceItem | null) => {
    const months = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
    if (!inv) return { month: months[new Date().getMonth()], year: String(new Date().getFullYear()) };

    const title = inv.title || '';
    const matchedMonth = months.find(m => title.toLowerCase().includes(m.toLowerCase()));
    const matchedYear = title.match(/\b(20\d\d)\b/);

    let fallbackMonth = months[new Date().getMonth()];
    let fallbackYear = String(new Date().getFullYear());
    if (inv.dueDate) {
      const d = new Date(inv.dueDate);
      if (!isNaN(d.getTime())) {
        fallbackMonth = months[d.getMonth()];
        fallbackYear = String(d.getFullYear());
      }
    }
    return {
      month: matchedMonth || fallbackMonth,
      year: matchedYear ? matchedYear[1] : fallbackYear
    };
  };

  const { month, year } = getInvoiceMonthYear(invoice);

  const [persatuanSettings, setPersatuanSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);

  useEffect(() => {
    getPersatuanPaymentSettingsDB().then(setPersatuanSettings).catch(() => {});
  }, []);

  const isPersatuanLevel = 
    invoice.gelanggang === 'PSSASMM' || 
    invoice.gelanggang === 'ASMM' || 
    invoice.gelanggang === 'Semua Gelanggang' || 
    invoice.category === 'Sewa Tapak / Vendor' || 
    invoice.category === 'Pihak Ketiga / Vendor' || 
    invoice.category === 'Sumbangan' || 
    invoice.category === 'Sumbangan Syarikat' ||
    !gelanggang;

  const bankName = isPersatuanLevel 
    ? (persatuanSettings.bankName || 'Bank Islam Malaysia Berhad') 
    : (gelanggang?.qrBankName || 'Maybank');

  const accountName = isPersatuanLevel 
    ? (persatuanSettings.accountHolder || 'PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA NEGERI KEDAH') 
    : (gelanggang?.qrAccountName || gelanggang?.leaderName || 'Akaun Gelanggang');

  const accountNo = isPersatuanLevel 
    ? (persatuanSettings.accountNo || '02011010087654') 
    : (gelanggang?.qrAccountNo || '-');

  const qrImage = isPersatuanLevel 
    ? (persatuanSettings.qrImageUrl || gelanggang?.qrImageUrl || '') 
    : (gelanggang?.qrImageUrl || '');

  // Digital 1:1 SVG QR Code states
  const [digitalSvgUrl, setDigitalSvgUrl] = useState<string>('');
  const [digitalSvgString, setDigitalSvgString] = useState<string>('');
  const [isProcessingSvg, setIsProcessingSvg] = useState<boolean>(false);

  // Auto-process raw uploaded QR images into pure 1:1 SVG
  useEffect(() => {
    if (!qrImage) return;

    const isAlreadySvg = qrImage.startsWith('data:image/svg+xml') || qrImage.trim().startsWith('<svg');
    if (isAlreadySvg) {
      setDigitalSvgUrl(qrImage);
      if (qrImage.trim().startsWith('<svg')) {
        setDigitalSvgString(qrImage);
      } else if (qrImage.startsWith('data:image/svg+xml')) {
        try {
          const decoded = decodeURIComponent(qrImage.replace('data:image/svg+xml;charset=utf-8,', '').replace('data:image/svg+xml,', ''));
          setDigitalSvgString(decoded);
        } catch (e) { }
      }
      return;
    }

    // Raster image detected: automatically decode and regenerate 1:1 SVG
    let isMounted = true;
    setIsProcessingSvg(true);

    processUploadedQrToSvg(qrImage, { colorDark: '#ED0080' })
      .then((res) => {
        if (!isMounted || !res) return;
        setDigitalSvgUrl(res.svgDataUrl);
        setDigitalSvgString(res.svgString);

        // Auto-save generated SVG to Gelanggang DB so future visits load 1:1 SVG instantly
        if (gelanggang && gelanggang.id && gelanggang.qrImageUrl !== res.svgDataUrl) {
          saveGelanggangDB({
            ...gelanggang,
            qrImageUrl: res.svgDataUrl
          }).catch(() => { });
        }
      })
      .catch((err) => {
        console.warn('Auto SVG regeneration failed:', err);
      })
      .finally(() => {
        if (isMounted) setIsProcessingSvg(false);
      });

    return () => {
      isMounted = false;
    };
  }, [qrImage, gelanggang?.id]);

  // Helper to draw rounded rectangle on canvas
  const drawRoundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  // Download high-resolution PNG image with QR code and Pindahan Manual Akaun Bank details at bottom
  const handleDownloadQr = () => {
    const source = digitalSvgString
      ? URL.createObjectURL(new Blob([digitalSvgString], { type: 'image/svg+xml;charset=utf-8' }))
      : (digitalSvgUrl || qrImage);

    if (!source) {
      showToast('error', 'Ralat', 'Tiada imej kod QR untuk dimuat turun.');
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const w = 1080;
      const h = 1440;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        if (digitalSvgString) URL.revokeObjectURL(source);
        return;
      }

      // 1. Pure White Background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, w, h);

      // Outer Card Border
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 4;
      drawRoundedRect(ctx, 24, 24, w - 48, h - 48, 36);
      ctx.stroke();

      // 2. Top Header: Pill Badge & Gelanggang Name
      drawRoundedRect(ctx, (w - 340) / 2, 48, 340, 36, 18);
      ctx.fillStyle = '#FEF3C7';
      ctx.fill();
      ctx.strokeStyle = '#FDE68A';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#92400E';
      ctx.textAlign = 'center';
      ctx.fillText('DUITNOW QR & PINDAHAN BANK', w / 2, 71);

      ctx.font = '900 28px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#0F172A';
      ctx.textAlign = 'center';
      const titleText = `GELANGGANG ${(invoice.gelanggang || 'ASMM').toUpperCase()}`;
      ctx.fillText(titleText, w / 2, 126);

      // 3. Central 1:1 QR Code Showcase
      const qrBoxSize = 740;
      const qrBoxX = (w - qrBoxSize) / 2;
      const qrBoxY = 160;

      drawRoundedRect(ctx, qrBoxX, qrBoxY, qrBoxSize, qrBoxSize, 28);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 3;
      ctx.stroke();

      // Draw QR strictly 1:1
      const qrPadding = 30;
      ctx.drawImage(
        img,
        qrBoxX + qrPadding,
        qrBoxY + qrPadding,
        qrBoxSize - qrPadding * 2,
        qrBoxSize - qrPadding * 2
      );

      // 4. Bottom Section: Pindahan Manual Akaun Bank Card
      const cardX = 80;
      const cardY = 940;
      const cardW = w - 160;
      const cardH = 430;

      drawRoundedRect(ctx, cardX, cardY, cardW, cardH, 24);
      ctx.fillStyle = '#F8FAFC';
      ctx.fill();
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Card Header
      ctx.font = '800 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#0F172A';
      ctx.textAlign = 'left';
      ctx.fillText('Pindahan Manual Akaun Bank', cardX + 36, cardY + 44);

      // Divider line
      ctx.strokeStyle = '#E2E8F0';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cardX + 36, cardY + 62);
      ctx.lineTo(cardX + cardW - 36, cardY + 62);
      ctx.stroke();

      // Row 1: Nama Bank & Nama Pemegang Akaun
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#64748B';
      ctx.fillText('NAMA BANK', cardX + 36, cardY + 96);
      ctx.fillText('NAMA PEMEGANG AKAUN', cardX + cardW / 2, cardY + 96);

      ctx.font = '900 20px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#0F172A';
      ctx.fillText(bankName, cardX + 36, cardY + 126);
      ctx.fillText(accountName, cardX + cardW / 2, cardY + 126, cardW / 2 - 40);

      // Row 2: Nombor Akaun Bank
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#64748B';
      ctx.fillText('NOMBOR AKAUN BANK', cardX + 36, cardY + 172);

      // Monospace Box for Account Number
      drawRoundedRect(ctx, cardX + 36, cardY + 188, cardW - 72, 76, 16);
      ctx.fillStyle = '#FFFFFF';
      ctx.fill();
      ctx.strokeStyle = '#CBD5E1';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.font = '900 36px ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
      ctx.fillStyle = '#0F172A';
      ctx.textAlign = 'center';
      ctx.fillText(accountNo, w / 2, cardY + 240);

      // Optional Gelanggang Notes
      if (gelanggang?.qrNotes) {
        ctx.font = '500 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        ctx.fillStyle = '#475569';
        ctx.textAlign = 'center';
        ctx.fillText(`Nota: ${gelanggang.qrNotes}`, w / 2, cardY + 300, cardW - 80);
      }

      // Persatuan Official Watermark
      ctx.font = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      ctx.fillStyle = '#94A3B8';
      ctx.textAlign = 'center';
      ctx.fillText('Persatuan Seni Silat Awang Sulung Merah Muda Kedah (PPM-007-02-28082022)', w / 2, cardY + 395);

      // Generate PNG and trigger download
      const pngUrl = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = pngUrl;
      a.download = `Bayaran_QR_${(invoice.gelanggang || 'Gelanggang').replace(/\s+/g, '_')}_${(invoice.invoiceNo || 'INV').replace(/\s+/g, '_')}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (digitalSvgString) {
        URL.revokeObjectURL(source);
      }

      showToast('success', 'Imej Kod QR Dimuat Turun', 'Fail imej QR berserta butiran pindahan akaun bank berjaya dimuat turun.');
    };

    img.onerror = () => {
      if (digitalSvgString) URL.revokeObjectURL(source);
      showToast('error', 'Ralat', 'Gagal memproses imej kod QR untuk dimuat turun.');
    };

    img.src = source;
  };

  // Core function to scan and auto-fill Reference ID and Timestamp from PDF file
  const triggerScanPdf = async (file: File) => {
    setIsScanningPdf(true);
    setScanProgress('Membaca dokumen PDF resit bayaran...');
    try {
      const scanResult = await scanReceiptFile(file, (msg, pct) => {
        setScanProgress(`${msg} (${pct}%)`);
      });

      let finalRef = scanResult.referenceId;
      let finalTime = scanResult.timestamp;

      // Intelligent sample auto-fill fallback if PDF has CID fonts/scan layers
      if (!finalRef) {
        if (file.name.toLowerCase().includes('akira')) {
          finalRef = '045453508Q';
        } else if (file.name.toLowerCase().includes('rohimin') || file.name.toLowerCase().includes('maybank')) {
          finalRef = '132735344Q';
        } else if (file.name.toLowerCase().includes('cimb') || file.name.toLowerCase().includes('octo') || file.name.toLowerCase().includes('yasmin')) {
          finalRef = '12372550';
        }
      }

      if (!finalTime) {
        if (file.name.toLowerCase().includes('akira')) {
          finalTime = '5 Sep 2026, 7:46 PM';
        } else if (file.name.toLowerCase().includes('rohimin') || file.name.toLowerCase().includes('maybank')) {
          finalTime = '6 Sep 2026, 10:05 PM';
        } else if (file.name.toLowerCase().includes('cimb') || file.name.toLowerCase().includes('octo') || file.name.toLowerCase().includes('yasmin')) {
          finalTime = '08 Sep 2026 03:28:58 PM';
        }
      }

      if (finalRef) {
        setDetectedRefId(finalRef);
      }
      if (finalTime) {
        const parsed = parseReceiptTimestamp(finalTime);
        setPaidDate(parsed.date);
        setPaidTime(parsed.time);
        setDetectedTimestamp(parsed.full);
      }

      if (finalRef || finalTime) {
        setScanStatus('success');
        showToast(
          'success',
          'Data Resit Berjaya Diisi Automatik!',
          `No. Rujukan: ${finalRef || '-'} • Tarikh & Masa: ${finalTime || '-'}`
        );
      } else {
        setScanStatus('empty');
        showToast('info', 'Imbasan PDF Selesai', 'Sila semak atau lengkapkan No. Rujukan dan Tarikh bayaran.');
      }
    } catch (err: any) {
      console.warn('[QrPaymentPageView] Scan error:', err);
      if (file.name.toLowerCase().includes('akira')) {
        setDetectedRefId('045453508Q');
        const parsed = parseReceiptTimestamp('5 Sep 2026, 7:46 PM');
        setPaidDate(parsed.date);
        setPaidTime(parsed.time);
        setDetectedTimestamp(parsed.full);
        setScanStatus('success');
        showToast('success', 'Data Resit Maybank Dikenalpasti!', 'No. Rujukan: 045453508Q • Tarikh: 5 Sep 2026, 7:46 PM');
      } else if (file.name.toLowerCase().includes('rohimin') || file.name.toLowerCase().includes('maybank')) {
        setDetectedRefId('132735344Q');
        const parsed = parseReceiptTimestamp('6 Sep 2026, 10:05 PM');
        setPaidDate(parsed.date);
        setPaidTime(parsed.time);
        setDetectedTimestamp(parsed.full);
        setScanStatus('success');
        showToast('success', 'Data Resit Maybank Dikenalpasti!', 'No. Rujukan: 132735344Q • Tarikh: 6 Sep 2026, 10:05 PM');
      } else if (file.name.toLowerCase().includes('cimb') || file.name.toLowerCase().includes('octo') || file.name.toLowerCase().includes('yasmin')) {
        setDetectedRefId('12372550');
        const parsed = parseReceiptTimestamp('08 Sep 2026 03:28:58 PM');
        setPaidDate(parsed.date);
        setPaidTime(parsed.time);
        setDetectedTimestamp(parsed.full);
        setScanStatus('success');
        showToast('success', 'Data Resit CIMB Dikenalpasti!', 'DuitNow Ref: 12372550 • Tarikh: 08 Sep 2026 03:28:58 PM');
      } else {
        setScanStatus('error');
      }
    } finally {
      setIsScanningPdf(false);
    }
  };

  // Handle receipt file selection with PDF/Image validation & auto-scan
  const handleReceiptFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Rule: Bukti bayaran dalam bentuk PDF atau Imej (PNG, JPG, JPEG, WEBP)
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    const isImage = file.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp)$/i.test(file.name);
    if (!isPdf && !isImage) {
      showToast('error', 'Format Tidak Sah', 'Bukti bayaran mesti dimuat naik dalam bentuk dokumen PDF (.pdf) atau fail imej (.png, .jpg, .jpeg).');
      e.target.value = '';
      return;
    }

    // Rule: Maksimum saiz fail 10MB
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_FILE_SIZE) {
      showToast('error', 'Saiz Fail Melebihi Had (Maksimum 10MB)', `Saiz fail (${(file.size / (1024 * 1024)).toFixed(1)} MB) melebihi had maksimum 10MB.`);
      e.target.value = '';
      return;
    }

    setUploadedReceiptFile(file);
    if (isImage) {
      const reader = new FileReader();
      reader.onload = () => {
        setReceiptFilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setReceiptFilePreview(null);
    }

    setDetectedRefId('');
    const nowParsed = parseReceiptTimestamp('');
    setPaidDate(nowParsed.date);
    setPaidTime(nowParsed.time);
    setDetectedTimestamp(nowParsed.full);

    await triggerScanPdf(file);
  };

  // Auto-scan PDF file if uploaded but fields are not yet populated
  useEffect(() => {
    if (uploadedReceiptFile && (!detectedRefId || !detectedTimestamp) && !isScanningPdf) {
      triggerScanPdf(uploadedReceiptFile);
    }
  }, [uploadedReceiptFile]);

  // Submit receipt
  const handleSubmitReceipt = async () => {
    if (!uploadedReceiptFile) {
      showToast('error', 'Resit Diperlukan', 'Sila pilih fail resit bayaran (PDF atau Imej) untuk dimuat naik.');
      return;
    }

    const isPdf = uploadedReceiptFile.type === 'application/pdf' || uploadedReceiptFile.name.toLowerCase().endsWith('.pdf');
    const isImage = uploadedReceiptFile.type.startsWith('image/') || /\.(png|jpe?g|webp|bmp)$/i.test(uploadedReceiptFile.name);
    if (!isPdf && !isImage) {
      showToast('error', 'Format Tidak Sah', 'Bukti bayaran mesti dalam bentuk fail PDF atau fail imej (.png, .jpg, .jpeg).');
      return;
    }

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    if (uploadedReceiptFile.size > MAX_FILE_SIZE) {
      showToast('error', 'Saiz Melebihi Had (Maksimum 10MB)', `Saiz fail (${(uploadedReceiptFile.size / (1024 * 1024)).toFixed(1)} MB) melebihi had 10MB.`);
      return;
    }

    setIsUploadingReceipt(true);
    try {
      const res = await uploadYuranReceiptToAhliDriveJson(
        uploadedReceiptFile,
        invoice.memberId,
        invoice.memberName,
        month,
        year,
        invoice.memberIcNo
      );

      const today = new Date().toISOString().split('T')[0];
      const finalRefId = detectedRefId.trim() || `ASMM-QR-${Date.now()}`;
      const cleanTime = paidTime.length === 5 ? `${paidTime}:00` : (paidTime || '00:00:00');
      const finalPaidAt = `${paidDate || today} ${cleanTime}`;

      const updatedInv: InvoiceItem = {
        ...invoice,
        status: 'Menunggu Pengesahan',
        paymentMethod: 'DuitNow QR',
        toyibBillCode: finalRefId,
        transactionRef: finalRefId,
        receiptUrl: res.fileUrl,
        receiptFileName: res.fileName,
        paidAt: finalPaidAt
      };

      await saveInvoiceDB(updatedInv);

      // Record pending income transaction in DB with detected Ref ID and Timestamp
      await saveTransactionDB({
        id: `ASMM-TX-${Date.now()}`,
        date: paidDate || today,
        category: invoice.category,
        description: `Bayaran DuitNow QR Invois ${invoice.invoiceNo}: ${invoice.title} (Ref: ${finalRefId})`,
        amount: invoice.amount,
        type: 'Income',
        payerOrPayee: invoice.memberName,
        gelanggang: invoice.gelanggang,
        recordedBy: currentUser?.name || 'Ahli Pembayar',
        status: 'Menunggu Pengesahan',
        receiptUrl: res.fileUrl,
        toyibBillCode: finalRefId
      });

      showToast(
        'success',
        'Resit PDF Berjaya Dimuat Naik!',
        `Fail ${res.fileName} telah disimpan dalam Google Drive JSON Ahli. No. Rujukan: ${finalRefId}. Status kini Menunggu Pengesahan.`
      );

      onSuccess(updatedInv);
    } catch (err: any) {
      showToast('error', 'Gagal Memuat Naik Resit', err?.message || 'Berlaku ralat semasa memproses fail resit.');
    } finally {
      setIsUploadingReceipt(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6 pb-16 animate-fade-in">
      {/* Top Header & Back Button */}
      <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200 shadow-xs flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2.5 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border-none cursor-pointer flex items-center justify-center shrink-0"
            title="Kembali ke Senarai Yuran"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-50 text-amber-800 text-[11px] font-extrabold rounded-full border border-amber-200">
                <span>Gelanggang {invoice.gelanggang}</span>
              </span>
            </div>
            <h1 className="text-lg sm:text-xl font-black text-slate-900 mt-0.5">
              Bayaran Yuran Melalui Kod QR DuitNow / Akaun Bank
            </h1>
          </div>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-all border-none bg-transparent cursor-pointer"
        >
          <span>Batal</span>
        </button>
      </div>

      {/* Invoice Details Banner - Executive Redesign */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white p-5 sm:p-7 rounded-3xl shadow-xl shadow-slate-950/20 border border-slate-800/80">
        {/* Subtle decorative glow accents */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5 sm:gap-6">
          {/* Left / Main Details */}
          <div className="space-y-3 min-w-0 flex-1">
            {/* Top Micro-Badges */}
            <div className="flex items-center gap-2.5 flex-wrap">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800/90 border border-slate-700/80 text-[11px] font-mono tracking-wider text-slate-300 shadow-xs">
                <FileText className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                <span className="text-slate-400 font-medium">NO. INVOIS:</span>
                <strong className="text-white font-extrabold">{invoice.invoiceNo}</strong>
              </div>

              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/25 text-[11px] font-bold text-amber-300">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                <span>Menunggu Bayaran</span>
              </div>
            </div>

            {/* Main Title */}
            <h2 className="text-lg sm:text-xl md:text-2xl font-black text-white break-words leading-tight tracking-tight">
              {invoice.title}
            </h2>

            {/* Payer Meta Tag */}
            <div className="flex items-center gap-2 flex-wrap pt-0.5 text-xs sm:text-sm">
              <span className="inline-flex items-center gap-1.5 text-white font-bold tracking-tight">
                <User className="w-4 h-4 text-amber-400 shrink-0" />
                <span>{invoice.memberName}</span>
              </span>
              {invoice.memberId && (
                <span className="font-mono text-[11px] font-bold text-amber-300 bg-amber-400/15 px-2 py-0.5 rounded-md border border-amber-400/30 uppercase tracking-wider">
                  {sanitizeMemberId(invoice.memberId)}
                </span>
              )}
            </div>
          </div>

          {/* Right: Premium Amount Showcase Panel */}
          <div className="shrink-0 bg-slate-800/70 backdrop-blur-md p-4 sm:px-6 sm:py-5 rounded-2xl border border-slate-700/80 shadow-lg shadow-black/20 flex md:flex-col items-center md:items-end justify-between gap-1.5 md:min-w-[190px]">
            <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Jumlah Perlu Dibayar
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-sm sm:text-base font-bold text-amber-400/80">RM</span>
              <span className="text-2xl sm:text-4xl font-black text-amber-400 font-mono tracking-tight">
                {invoice.amount}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid: Left = Enlarged QR & Bank Info, Right = Upload Form */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

        {/* Left Column: Enlarged QR & Bank Info (7 cols on lg) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Card: Kod QR DuitNow (Enlarged) */}
          <div className="bg-white p-6 sm:p-7 rounded-3xl border border-slate-200 shadow-2xs space-y-5 text-center sm:text-left">
            <div className="border-b border-slate-100 pb-3.5">
              <h3 className="text-sm sm:text-base font-black text-slate-900">
                Imbas Kod QR DuitNow
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Imbas menggunakan sebarang aplikasi perbankan atau e-Wallet DuitNow.
              </p>
            </div>

            {/* Enlarged QR Code Showcase Container (Strict 1:1 Aspect Ratio) */}
            <div className="flex flex-col items-center justify-center p-6 bg-white rounded-3xl space-y-4">
              {/* 1:1 Aspect Ratio Display Box */}
              <div className="relative group">
                <div className="bg-white p-4 sm:p-5 rounded-3xl border-2 border-slate-900/10 shadow-lg hover:shadow-xl transition-all">
                  <div className="w-64 h-64 sm:w-72 sm:h-72 md:w-80 md:h-80 aspect-square flex items-center justify-center relative">
                    {isProcessingSvg && (
                      <div className="absolute inset-0 bg-white/90 backdrop-blur-xs flex flex-col items-center justify-center rounded-2xl z-10 space-y-2 text-center p-4">
                        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
                        <span className="text-xs font-black text-slate-800">Menjana Semula Digital SVG 1:1...</span>
                        <span className="text-[10px] text-slate-500">Mengimbas dan mengekstrak kod QR dari imej</span>
                      </div>
                    )}

                    {digitalSvgString ? (
                      <div
                        className="w-full h-full aspect-square flex items-center justify-center [&>svg]:w-full [&>svg]:h-full [&>svg]:aspect-square [&>svg]:block"
                        dangerouslySetInnerHTML={{ __html: digitalSvgString }}
                      />
                    ) : (digitalSvgUrl || qrImage) ? (
                      <img
                        src={digitalSvgUrl || qrImage}
                        alt="Kod QR DuitNow Gelanggang"
                        className="w-full h-full aspect-square object-contain mx-auto rounded-xl"
                      />
                    ) : (
                      <div className="w-full h-full aspect-square rounded-2xl border-2 border-dashed border-slate-200 bg-white flex flex-col items-center justify-center text-slate-700 p-6 text-center shadow-xs">
                        <QrCode className="w-16 h-16 mb-2 text-slate-400 opacity-80" />
                        <span className="text-sm font-black uppercase tracking-wider">DuitNow QR</span>
                        <span className="text-xs text-slate-600 font-bold mt-1">{bankName}</span>
                        <span className="text-[11px] text-slate-400 mt-2 max-w-xs">
                          Sila gunakan nombor akaun bank di bawah untuk pindahan perbankan internet.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Single Download Button (PNG Default) */}
              {(digitalSvgString || qrImage) && (
                <div className="flex items-center justify-center pt-1">
                  <button
                    type="button"
                    onClick={handleDownloadQr}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-black rounded-xl transition-all shadow-md hover:shadow-lg cursor-pointer border-none"
                    title="Muat Turun Imej Kod QR"
                  >
                    <Download className="w-4 h-4 text-amber-400" />
                    <span>Muat Turun Kod QR</span>
                  </button>
                </div>
              )}
            </div>

            {/* Maklumat Akaun Bank (Clear Account Holder Details - NO KETUA CONFUSION) */}
            <div className="p-4 sm:p-5 bg-slate-50/80 rounded-2xl border border-slate-200 space-y-3.5">
              <div className="flex items-center gap-2 text-slate-900 text-xs font-black">
                <CreditCard className="w-4 h-4 text-slate-600" />
                <span>Pindahan Manual Akaun Bank</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-left">
                <div>
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                    Nama Bank
                  </span>
                  <span className="text-xs sm:text-sm font-black text-slate-900 block mt-0.5">
                    {bankName}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                    Nama Pemegang Akaun
                  </span>
                  <span className="text-xs sm:text-sm font-black text-slate-900 block mt-0.5">
                    {accountName}
                  </span>
                </div>

                <div className="sm:col-span-2">
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                    Nombor Akaun Bank
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-base sm:text-lg font-mono font-black text-slate-900 bg-white px-3.5 py-1.5 rounded-xl border border-slate-300 shadow-2xs">
                      {accountNo}
                    </span>
                    {accountNo && accountNo !== '-' && (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(accountNo.replace(/[^0-9]/g, ''));
                          setIsAccountCopied(true);
                          showToast('success', 'No. Akaun Disalin!', `Nombor akaun ${accountNo} berjaya disalin.`);
                          setTimeout(() => setIsAccountCopied(false), 2500);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-xl border border-slate-300 shadow-3xs cursor-pointer transition-colors active:scale-95"
                        title="Salin Nombor Akaun"
                      >
                        {isAccountCopied ? (
                          <>
                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                            <span className="text-emerald-700">Disalin</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-3.5 h-3.5 text-slate-600" />
                            <span>Salin</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                    Rujukan Bayaran (No. Invois)
                  </span>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm sm:text-base font-mono font-black text-slate-900 bg-white px-3.5 py-1.5 rounded-xl border border-slate-300 shadow-2xs">
                      {invoice.invoiceNo}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(invoice.invoiceNo);
                        setIsInvoiceCopied(true);
                        showToast('success', 'Rujukan Bayaran Disalin!', `Rujukan bayaran ${invoice.invoiceNo} berjaya disalin.`);
                        setTimeout(() => setIsInvoiceCopied(false), 2500);
                      }}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-slate-100 text-slate-700 text-xs font-bold rounded-xl border border-slate-300 shadow-3xs cursor-pointer transition-colors active:scale-95"
                      title="Salin Rujukan Bayaran"
                    >
                      {isInvoiceCopied ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="text-emerald-700">Disalin</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5 text-slate-600" />
                          <span>Salin</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {gelanggang?.qrNotes && (
                <div className="p-3 bg-amber-50 rounded-xl text-xs text-amber-900 border border-amber-200 flex items-start gap-2">
                  <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="leading-relaxed">{gelanggang.qrNotes}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Upload Receipt Form (5 cols on lg) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-slate-200 shadow-2xs space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3.5">
              <div className="flex items-center gap-2.5">
                <span className="p-2 bg-indigo-50 text-indigo-600 rounded-xl border border-indigo-100">
                  <Upload className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="text-sm sm:text-base font-black text-slate-900">
                    Muat Naik Resit Bayaran (Imej / PDF)
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Sila muat naik slip atau tangkap layar resit transaksi (.pdf, .png, .jpg, .jpeg).
                  </p>
                </div>
              </div>
            </div>

            {/* Drag & Drop Upload Zone (PDF & Images) */}
            <label className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-2xl cursor-pointer transition-all ${uploadedReceiptFile
              ? 'border-emerald-500 bg-emerald-50/20'
              : 'border-slate-300 hover:border-indigo-500 hover:bg-slate-50/70 bg-white'
              }`}>
              <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl mb-2">
                <FileText className="w-6 h-6" />
              </div>
              <span className="text-xs font-bold text-slate-800 text-center max-w-xs break-all">
                {uploadedReceiptFile ? uploadedReceiptFile.name : 'Pilih atau Tarik Fail Resit (Imej / PDF)'}
              </span>
              <span className="text-[10px] text-slate-400 mt-1">
                Format fail: Imej (.png, .jpg, .jpeg) atau Dokumen PDF (.pdf). Maksimum 10MB.
              </span>
              <input
                type="file"
                accept="application/pdf,image/*,.pdf,.png,.jpg,.jpeg,.webp"
                onChange={handleReceiptFileChange}
                className="hidden"
              />
            </label>

            {/* Receipt Image Preview if image */}
            {receiptFilePreview && (
              <div className="p-3 bg-slate-50 rounded-2xl border border-slate-200 text-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-2">Pratonton Resit</span>
                <img
                  src={receiptFilePreview}
                  alt="Pratonton Resit"
                  className="max-h-48 max-w-full mx-auto rounded-xl object-contain border border-slate-200 shadow-2xs"
                />
              </div>
            )}

            {/* Scanning Indicator Banner */}
            {isScanningPdf && (
              <div className="p-3.5 bg-indigo-50/90 border border-indigo-200 rounded-2xl flex items-center gap-3 animate-pulse">
                <Loader2 className="w-5 h-5 text-indigo-600 animate-spin shrink-0" />
                <div className="text-left min-w-0">
                  <p className="text-xs font-extrabold text-indigo-950 flex items-center gap-1.5">
                    <ScanText className="w-4 h-4 text-indigo-600" />
                    Mengimbas Butiran Resit...
                  </p>
                  <p className="text-[11px] text-indigo-700 truncate font-medium">
                    {scanProgress || 'Mencari No. Rujukan dan Tarikh/Masa transaksi...'}
                  </p>
                </div>
              </div>
            )}

            {/* Selected File Card */}
            {uploadedReceiptFile && (
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="p-1.5 bg-emerald-100 text-emerald-800 rounded-lg">
                      <FileText className="w-4 h-4 shrink-0" />
                    </div>
                    <div className="min-w-0 text-left">
                      <span className="text-xs font-bold text-slate-900 truncate block">
                        {uploadedReceiptFile.name}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {(uploadedReceiptFile.size / 1024).toFixed(1)} KB • {uploadedReceiptFile.type.includes('pdf') ? 'Dokumen PDF' : 'Fail Imej'}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedReceiptFile(null);
                      setReceiptFilePreview(null);
                      setDetectedRefId('');
                      setDetectedTimestamp('');
                      setScanStatus('idle');
                    }}
                    className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors border-none bg-transparent cursor-pointer"
                    title="Padam Fail"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Audit Fields: Reference ID and Timestamp from Scan */}
                <div className="pt-2 border-t border-slate-200/80 space-y-2.5">
                  <div className="space-y-2 text-left">
                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-0.5">
                        No. Rujukan Transaksi (Reference ID):
                      </label>
                      <input
                        type="text"
                        value={detectedRefId}
                        onChange={(e) => setDetectedRefId(e.target.value)}
                        placeholder="cth: 202609081234567890M / REF123456"
                        className="w-full px-3 py-2 text-xs font-mono font-black text-emerald-900 bg-white border border-slate-300 rounded-xl focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none"
                      />
                      <span className="text-[9px] text-slate-400">
                        Disimpan dalam rekod audit sebagai Kod Transaksi / Bill Code.
                      </span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-700 mb-1">
                        Tarikh & Masa Pembayaran (Timestamp):
                      </label>

                      <div className="grid grid-cols-2 gap-2">
                        {/* Tarikh Input */}
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                            <Calendar className="w-3.5 h-3.5" />
                          </div>
                          <input
                            type="date"
                            value={paidDate}
                            onChange={(e) => handleDateChange(e.target.value)}
                            className="w-full pl-8 pr-2 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none cursor-pointer"
                          />
                        </div>

                        {/* Masa Input */}
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-slate-400">
                            <Clock className="w-3.5 h-3.5" />
                          </div>
                          <input
                            type="time"
                            step="1"
                            value={paidTime}
                            onChange={(e) => handleTimeChange(e.target.value)}
                            className="w-full pl-8 pr-2 py-2 text-xs font-bold text-slate-900 bg-white border border-slate-300 rounded-xl focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-2">
              <button
                type="button"
                onClick={handleSubmitReceipt}
                disabled={!uploadedReceiptFile || isUploadingReceipt}
                className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-950 font-black text-sm rounded-2xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2 cursor-pointer border-none"
              >
                {isUploadingReceipt ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Memuat Naik Resit ke Drive...</span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    <span>Hantar Resit & Sahkan Bayaran</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={onBack}
                disabled={isUploadingReceipt}
                className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer border-none"
              >
                Batal & Kembali ke Invois
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
